import {
  Array,
  Boolean,
  Literal,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { ModelFeatures } from "./ModelFeatures.js";

/**
 * Identifies which gateway/provider a chat-completions deployment talks to.
 *
 * Each value here MUST have a `case` in `resolveChatCompletionsEndpoint` in
 * `packages/server/src/magic/providers/providerClientUtils.ts`. That switch
 * is exhaustively typed so adding a new gateway here will fail the build
 * until the credential lookup (and any per-stack gating) is wired up. Baseten
 * gateways use `Config.baseten.apiKey`; Bedrock uses the stack's ConfigV2
 * settings and ambient IAM credentials.
 *
 * This is intentionally separate from `AiClientType`. `AiClientType`
 * describes the high-level *agent* (how Hex talks to a model family);
 * `ChatCompletionsGateway` only disambiguates which OpenAI-compatible
 * gateway sits behind a single `AiClientType.OPENAI_CHAT_COMPLETIONS` agent.
 *
 * Baseten gateways read the request URL from the deployment config's
 * `baseURL`. Bedrock derives its regional endpoint from ConfigV2.
 */
export const ChatCompletionsGatewayLiteral = Union(
  Literal("BASETEN"),
  Literal("BASETEN_DEDICATED"),
  Literal("BEDROCK"),
);
export const ChatCompletionsGateway = getNormalEnum(
  ChatCompletionsGatewayLiteral,
);
export type ChatCompletionsGateway = Static<
  typeof ChatCompletionsGatewayLiteral
>;

export const ChatCompletionsStackGeographyLiteral = Union(
  Literal("US"),
  Literal("EU"),
);
export type ChatCompletionsStackGeography = Static<
  typeof ChatCompletionsStackGeographyLiteral
>;

/**
 * Agent-side manual compaction for chat-completions deployments that lack
 * provider-native compaction (e.g. Kimi via Baseten).
 */
export const ChatCompletionsManualCompactionConfig = RRecord({
  enabled: Boolean,
  compactThresholdTokens: Number,
});
export type ChatCompletionsManualCompactionConfig = Static<
  typeof ChatCompletionsManualCompactionConfig
>;

export const KIMI_DEFAULT_MANUAL_COMPACTION_CONFIG: ChatCompletionsManualCompactionConfig =
  {
    enabled: true,
    compactThresholdTokens: 180_000,
  };

/**
 * Gateway deployment: API `model` string plus local budgeting fields.
 * Intentionally separate from OpenAI catalog models.
 *
 * `baseURL` is the gateway URL for this deployment (e.g.
 * `https://inference.baseten.co/v1` for serverless, or a
 * `model-<id>.api.baseten.co/...` host for a dedicated deployment).
 * Required at runtime for gateway-hosted deployments. Bedrock derives its
 * endpoint from the configured stack region.
 */
export const ChatCompletionsDeploymentConfig = RRecord({
  model: String,
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number),
  baseURL: Optional(String),
  /**
   * Selects which set of stack credentials to authenticate this deployment.
   * See {@link ChatCompletionsGateway}. Required at the application level;
   * modeled as Optional in the schema only so deployments persisted before
   * this field was introduced still deserialize. The runtime factory
   * (`createAiSdkChatCompletionsClient`) treats a missing value as a hard
   * error rather than silently falling back to a default credential.
   */
  gateway: Optional(ChatCompletionsGatewayLiteral),
  /**
   * Stack geographies where this specific deployment is reachable. When
   * absent, the deployment is available in every stack geography supported by
   * its gateway.
   */
  supportedStackGeographies: Optional(
    Array(ChatCompletionsStackGeographyLiteral),
  ),
  /**
   * Set to `true` for deployments that accept non-text content parts on
   * `user` messages — `image_url` (vision) and `file` (PDF/document input).
   *
   * When `false` or omitted, the message conformer replaces image/file
   * context and image tool results with short text placeholders rather than
   * dumping base64 as plain text or sending part shapes the gateway will
   * reject.
   *
   * Today there are no deployments that support one but not the other, so a
   * single flag is sufficient. If a future gateway splits these (e.g.
   * accepts PDFs but not images), introduce a separate `supportsFileInput`
   * field rather than overloading this one.
   */
  supportsVision: Optional(Boolean),
  /**
   * Override the `model` string sent on the wire without changing the
   * canonical model identifier used for billing, logging, and config.
   *
   * Use only for gateway-side workarounds where the serving endpoint requires
   * a different model string than the one that identifies the deployment — for
   * example, a Baseten dedicated deployment that serves K2.7 but whose vision
   * encoder is misconfigured to only accept requests with `model = "K2.6"`.
   * When absent, the canonical `model` field is used as-is.
   */
  wireModel: Optional(String),
  /**
   * Extra fields merged into the `/v1/chat/completions` request body at the
   * top level. Use for non-standard passthrough params accepted by specific
   * gateways — for example Baseten/Kimi's
   * `{ chat_template_kwargs: { enable_thinking: true } }` to enable thinking.
   * Mirrors the Python OpenAI SDK's `extra_body` argument.
   */
  extraBody: Optional(Unknown),
  /**
   * When set, enables agent-side preflight compaction for this deployment.
   * Reference a named default (e.g. {@link KIMI_DEFAULT_MANUAL_COMPACTION_CONFIG})
   * rather than inlining when possible.
   */
  manualCompactionConfig: Optional(ChatCompletionsManualCompactionConfig),
});
export type ChatCompletionsDeploymentConfig = Static<
  typeof ChatCompletionsDeploymentConfig
>;

/**
 * Parameters for `/v1/chat/completions` against an OpenAI-*compatible* gateway.
 */
export const ChatCompletionsModelParams = RRecord({
  frequencyPenalty: Optional(Number),
  n: Number,
  presencePenalty: Optional(Number),
  stopWords: Optional(Array(String)),
  temperature: Optional(Number),
  topP: Number,
  model: ChatCompletionsDeploymentConfig,
});
export type ChatCompletionsModelParams = Static<
  typeof ChatCompletionsModelParams
>;

/**
 * Billable model identifiers for Kimi models routed through the CHAT_COMPLETIONS
 * / Baseten gateway. These are used as keys in the credit pricing table and
 * stored in usage-log records, so values must never be renamed once in use.
 */
export const KimiModelLiteral = Union(Literal("KIMI_K_2_7_THINKING"));
export type KimiModel = Static<typeof KimiModelLiteral>;
export const KimiModel = getNormalEnum(KimiModelLiteral);

type KimiModelFeatures = ModelFeatures;
export const KimiModelFeatureMap: Record<KimiModel, KimiModelFeatures> = {
  [KimiModel.KIMI_K_2_7_THINKING]: {
    name: "moonshotai/Kimi-K2.7-Code",
    label: "Kimi K2.7",
    requiresDataRetention: false,
  },
};

/**
 * Billable model identifiers for Qwen models routed through the
 * CHAT_COMPLETIONS / Baseten gateway. Same immutability rule as
 * {@link KimiModelLiteral}: these are credit-pricing keys and are persisted in
 * usage-log records, so values must never be renamed once in use.
 */
export const QwenModelLiteral = Union(Literal("QWEN_3_6_35B_A3B"));
export type QwenModel = Static<typeof QwenModelLiteral>;
export const QwenModel = getNormalEnum(QwenModelLiteral);

type QwenModelFeatures = ModelFeatures;
export const QwenModelFeatureMap: Record<QwenModel, QwenModelFeatures> = {
  [QwenModel.QWEN_3_6_35B_A3B]: {
    name: "Qwen/Qwen3.6-35B-A3B-NVFP4",
    label: "Qwen 3.6",
    requiresDataRetention: false,
  },
};

export const GrokModelLiteral = Union(Literal("GROK_4_6"));
export type GrokModel = Static<typeof GrokModelLiteral>;
export const GrokModel = getNormalEnum(GrokModelLiteral);

type GrokModelFeatures = ModelFeatures;
export const GrokModelFeatureMap: Record<GrokModel, GrokModelFeatures> = {
  [GrokModel.GROK_4_6]: {
    name: "us.xai.grok-4.6",
    label: "Grok 4.6",
    requiresDataRetention: false,
  },
};

/**
 * Every model served over an OpenAI-compatible chat-completions gateway.
 * Consumers that only care "is this a chat-completions model" (billing,
 * wire-name reverse lookup) should use this rather than enumerating the
 * per-family unions.
 */
export const ChatCompletionsModelLiteral = Union(
  ...KimiModelLiteral.alternatives,
  ...QwenModelLiteral.alternatives,
  ...GrokModelLiteral.alternatives,
);
export type ChatCompletionsModel = Static<typeof ChatCompletionsModelLiteral>;
export const ChatCompletionsModel = getNormalEnum(ChatCompletionsModelLiteral);

export const ChatCompletionsModelFeatureMap: Record<
  ChatCompletionsModel,
  ModelFeatures
> = {
  ...KimiModelFeatureMap,
  ...QwenModelFeatureMap,
  ...GrokModelFeatureMap,
};
